// toBinaryLine is derived from src/decoder/bresenham.js of
// @ericblade/quagga2, under the MIT License.

import { ColorSpace } from "./locator/color-spaces";
import { grayscaleFromRGBA } from "./locator/grayscale";

// Copyright (c) 2014 Christoph Oberhofer, (c) 2019 Eric Blade and all other
// contributors

enum SlopeDirection {
    Up = 1,
    Down = -1,
}

export interface BarcodeLine {
    line: number[];
    min: number;
    max: number;
}

export function getBarcodeLineFromImageDataLine(
    imageData: ImageData,
    colorSpace: ColorSpace,
    y: number,
    x0: number,
    x1: number
): BarcodeLine {
    const line: number[] = [];
    let min = 255;
    let max = 0;
    if (x0 > x1) {
        const tmp = x1;
        x1 = x0;
        x0 = tmp;
    }
    const yOffset = y * imageData.width;
    const { data } = imageData;
    for (let x = x0; x <= x1; x++) {
        const offset = (yOffset + x) * 4;
        const val = grayscaleFromRGBA(colorSpace, data[offset], data[offset + 1], data[offset + 2], data[offset + 3]);
        min = val < min ? val : min;
        max = val > max ? val : max;
        line.push(val);
    }
    return {
        line,
        min,
        max,
    };
}

export function toBinaryLine(result: BarcodeLine): number[] {
    const { min } = result;
    const { max } = result;
    const { line } = result;
    let slope;
    let slope2;
    const center = min + (max - min) / 2;
    const extrema = [];
    let currentDir;
    let dir;
    let threshold = (max - min) / 12;
    const rThreshold = -threshold;
    let i;
    let j;

    // 1. find extrema
    currentDir = line[0] > center ? SlopeDirection.Up : SlopeDirection.Down;
    extrema.push({
        pos: 0,
        val: line[0],
    });
    for (i = 0; i < line.length - 2; i++) {
        slope = line[i + 1] - line[i];
        slope2 = line[i + 2] - line[i + 1];
        if (slope + slope2 < rThreshold && line[i + 1] < center * 1.5) {
            dir = SlopeDirection.Down;
        } else if (slope + slope2 > threshold && line[i + 1] > center * 0.5) {
            dir = SlopeDirection.Up;
        } else {
            dir = currentDir;
        }

        if (currentDir !== dir) {
            extrema.push({
                pos: i,
                val: line[i],
            });
            currentDir = dir;
        }
    }
    extrema.push({
        pos: line.length,
        val: line[line.length - 1],
    });

    for (j = extrema[0].pos; j < extrema[1].pos; j++) {
        line[j] = line[j] > center ? 0 : 1;
    }

    // iterate over extrema and convert to binary based on avg between minmax
    for (i = 1; i < extrema.length - 1; i++) {
        if (extrema[i + 1].val > extrema[i].val) {
            threshold = (extrema[i].val + ((extrema[i + 1].val - extrema[i].val) / 3) * 2) | 0;
        } else {
            threshold = (extrema[i + 1].val + (extrema[i].val - extrema[i + 1].val) / 3) | 0;
        }

        for (j = extrema[i].pos; j < extrema[i + 1].pos; j++) {
            line[j] = line[j] > threshold ? 0 : 1;
        }
    }

    return line;
}
